import { useEffect } from 'react'
import { AlertCircleIcon } from 'lucide-react'
import { type Control, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
	Alert, AlertDescription,
	DatePicker,
	Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
	FormDialog,
	Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Textarea,
} from '@rentflowdev/ui'
import { taskCreateSchema, TASK_KIND, TASK_BINDING_KIND } from '@rentflowdev/api'
import {
	useCreateTaskMutation,
	useUpdateTaskMutation,
	useListTaskResponsiblesQuery,
	useListPropertiesQuery,
	useListDealsQuery,
	useListContactsQuery,
	useListRentalsQuery,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import type { Task, TaskBindingKind } from '@/types'

const schema = taskCreateSchema
	.pick({ kind: true, title: true, description: true, responsibleId: true, binding: true })
	.extend({ dueDate: z.date().optional() })

type FormValues = z.infer<typeof schema>

interface TaskFormDialogProps {
	open:            boolean
	onOpenChange:    (open: boolean) => void
	task?:           Task
	initialBinding?: { kind: TaskBindingKind; id: string }
	lockBinding?:    boolean
}

function BindingIdSelect({
	control,
	bindingKind,
}: {
	 
	control: Control<FormValues, any, FormValues>
	bindingKind: TaskBindingKind
}) {
	const { t } = useTranslation()

	const { data: propertiesData, isLoading: isLoadingProperties } = useListPropertiesQuery(undefined, { skip: bindingKind !== 'PROPERTY' })
	const { data: dealsData,      isLoading: isLoadingDeals }      = useListDealsQuery(undefined,      { skip: bindingKind !== 'DEAL' })
	const { data: contactsData,   isLoading: isLoadingContacts }   = useListContactsQuery(undefined,   { skip: bindingKind !== 'CONTACT' })
	const { data: rentalsData,    isLoading: isLoadingRentals }    = useListRentalsQuery(undefined,    { skip: bindingKind !== 'RENTAL' })

	const isLoadingOptions = isLoadingProperties || isLoadingDeals || isLoadingContacts || isLoadingRentals

	const options: { id: string; label: string }[] = (() => {
		switch (bindingKind) {
			case 'PROPERTY':
				return (propertiesData?.properties ?? []).map((p) => ({ id: p.id, label: p.address }))
			case 'DEAL':
				return (dealsData?.items ?? []).map((d) => ({
					id:    d.id,
					label: d.clientName ?? d.propertyTitle ?? d.propertyAddress ?? d.id,
				}))
			case 'CONTACT':
				return (contactsData?.items ?? []).map((c) => ({ id: c.id, label: c.name }))
			case 'RENTAL':
				return (rentalsData?.rentals ?? []).map((r) => ({
					id:    r.id,
					label: r.propertyAddress ?? r.propertyTitle ?? r.id,
				}))
		}
	})()

	return (
		<FormField
			control={control}
			name="binding.id"
			render={({ field }) => (
				<FormItem className="flex-1">
					<FormLabel>{t(`tasks.bindingKinds.${bindingKind}`)}</FormLabel>
					<Select
						value={field.value ?? '__none__'}
						onValueChange={(v) => field.onChange(v === '__none__' ? undefined : v)}
						disabled={isLoadingOptions}
					>
						<FormControl>
							<SelectTrigger>
								<SelectValue placeholder={isLoadingOptions ? t('common.loading') : t('tasks.filters.binding')} />
							</SelectTrigger>
						</FormControl>
						<SelectContent>
							<SelectItem value="__none__">—</SelectItem>
							{options.map((o) => (
								<SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}

export function TaskFormDialog({ open, onOpenChange, task, initialBinding, lockBinding }: TaskFormDialogProps) {
	const { t } = useTranslation()
	const isEdit = !!task

	const [createTask, { isLoading: isCreating }] = useCreateTaskMutation()
	const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation()
	const { data: responsiblesData } = useListTaskResponsiblesQuery()
	const responsibles = responsiblesData?.responsibles ?? []

	const defaultBinding = task?.binding
		? { kind: task.binding.kind, id: task.binding.id }
		: initialBinding

	const form = useForm<FormValues, unknown, FormValues>({
		 
		resolver: zodResolver(schema) as any,
		defaultValues: {
			kind:          task?.kind        ?? 'CALL',
			title:         task?.title       ?? '',
			description:   task?.description ?? '',
			responsibleId: task?.responsibleId,
			dueDate:       task?.dueDate ? new Date(task.dueDate) : undefined,
			binding:       defaultBinding,
		},
	})

	useEffect(() => {
		if (open) {
			const binding = task?.binding
				? { kind: task.binding.kind, id: task.binding.id }
				: initialBinding
			form.reset({
				kind:          task?.kind        ?? 'CALL',
				title:         task?.title       ?? '',
				description:   task?.description ?? '',
				responsibleId: task?.responsibleId,
				dueDate:       task?.dueDate ? new Date(task.dueDate) : undefined,
				binding,
			})
		}
	}, [open, task])  // eslint-disable-line react-hooks/exhaustive-deps

	const bindingKind = useWatch({ control: form.control, name: 'binding.kind' })

	async function onSubmit(values: FormValues) {
		const binding = values.binding?.kind && values.binding?.id
			? { kind: values.binding.kind, id: values.binding.id }
			: undefined

		const body = {
			kind:          values.kind,
			title:         values.title?.trim() || undefined,
			description:   values.description,
			responsibleId: values.responsibleId || undefined,
			dueDate:       values.dueDate?.toISOString(),
			binding,
		}

		try {
			if (isEdit) {
				await updateTask({ id: task.id, body }).unwrap()
			} else {
				await createTask(body).unwrap()
			}
			onOpenChange(false)
		} catch (err) {
			form.setError('root', { message: extractErrorMessage(err) ?? 'Ошибка при сохранении' })
		}
	}

	return (
		<FormDialog
			open={open}
			onOpenChange={onOpenChange}
			title={isEdit ? t('tasks.actions.edit') : t('tasks.create')}
			submitLabel={isEdit ? 'Сохранить' : 'Создать'}
			cancelLabel={t('tasks.actions.cancel')}
			isSubmitting={isCreating || isUpdating}
			onSubmit={form.handleSubmit(onSubmit)}
		>
			<Form {...form}>
				<div className="flex flex-col gap-4">
					{form.formState.errors.root?.message && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertDescription>{form.formState.errors.root.message}</AlertDescription>
						</Alert>
					)}

					<div className="flex gap-3">
						<FormField
							control={form.control}
							name="kind"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('tasks.columns.kind')}</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger><SelectValue /></SelectTrigger>
										</FormControl>
										<SelectContent>
											{TASK_KIND.map((k) => (
												<SelectItem key={k} value={k}>{t(`tasks.kinds.${k}`)}</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="title"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('tasks.columns.title')}</FormLabel>
									<FormControl>
										<Input {...field} placeholder={t('tasks.titlePlaceholder')} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('tasks.columns.description')}</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										rows={3}
										placeholder={t('tasks.descriptionPlaceholder')}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex gap-3">
						<FormField
							control={form.control}
							name="responsibleId"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('tasks.columns.responsible')}</FormLabel>
									<Select value={field.value ?? '__none__'} onValueChange={(v) => field.onChange(v === '__none__' ? undefined : v)}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder={t('tasks.filters.allResponsibles')} />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value="__none__">{t('tasks.filters.allResponsibles')}</SelectItem>
											{responsibles.map((r) => (
												<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="dueDate"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('tasks.columns.due')}</FormLabel>
									<FormControl>
										<DatePicker
											value={field.value}
											onChange={field.onChange}
											placeholder="Выберите дату"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					{!lockBinding && (
						<div className="flex gap-3">
							<FormField
								control={form.control}
								name="binding.kind"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>{t('tasks.columns.binding')}</FormLabel>
										<Select
											value={field.value ?? '__none__'}
											onValueChange={(v) => {
												const kind = v === '__none__' ? undefined : v as TaskBindingKind
												field.onChange(kind)
												form.setValue('binding.id', undefined as unknown as string)
											}}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="—" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="__none__">—</SelectItem>
												{TASK_BINDING_KIND.map((k) => (
													<SelectItem key={k} value={k}>{t(`tasks.bindingKinds.${k}`)}</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
							{bindingKind && (
								<BindingIdSelect control={form.control as Control<FormValues, any, FormValues>} bindingKind={bindingKind} />
							)}
						</div>
					)}
				</div>
			</Form>
		</FormDialog>
	)
}
